import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellRing, Volume2, VolumeX, Clock, BadgeCheck, Wallet, Handshake } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playChime, subscribeAdminPush, pushSupported as isPushSupported } from "@/lib/push";

const SOUND_KEY = "sl_admin_sound";
const PUSH_KEY = "sl_admin_push";

export default function AdminAlerts() {
  const [pending, setPending] = useState(0);
  const [pendingPayouts, setPendingPayouts] = useState(0);
  const [pendingVerifications, setPendingVerifications] = useState(0);
  const [pendingPartners, setPendingPartners] = useState(0);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_KEY) !== "0");
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const prevPending = useRef(null);
  const prevPayouts = useRef(null);
  const prevVerifications = useRef(null);
  const prevPartners = useRef(null);
  const baseline = useRef(null);
  const pollCount = useRef(0);
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  const pushSupported = isPushSupported();

  // Poll global pending count every 20s ; ring on new + reminder while new ones untreated
  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      const count = data?.by_status?.en_attente?.count ?? 0;
      const payoutsCount = data?.pending_payouts ?? 0;
      const verifCount = data?.pending_verifications ?? 0;
      const partnersCount = data?.pending_partners ?? 0;
      const prev = prevPending.current;
      const prevP = prevPayouts.current;
      const prevV = prevVerifications.current;
      const prevPa = prevPartners.current;
      prevPending.current = count;
      prevPayouts.current = payoutsCount;
      prevVerifications.current = verifCount;
      prevPartners.current = partnersCount;
      setPending(count);
      setPendingPayouts(payoutsCount);
      setPendingVerifications(verifCount);
      setPendingPartners(partnersCount);
      pollCount.current += 1;
      if (baseline.current === null || count < baseline.current) baseline.current = count;
      if (!soundRef.current) return;
      if (prev !== null && count > prev) {
        playChime();
        toast.warning(`🔔 Nouvelle transaction en attente (${count} au total)`);
      } else if (prevV !== null && verifCount > prevV) {
        playChime();
        toast.warning(`🪪 Nouvelle vérification KYC à traiter (${verifCount} au total)`);
      } else if (prevP !== null && payoutsCount > prevP) {
        playChime();
        toast.warning(`💰 Nouvelle demande de virement parrainage (${payoutsCount} au total)`);
      } else if (prevPa !== null && partnersCount > prevPa) {
        playChime();
        toast.warning(`🤝 Nouvelle candidature partenaire (${partnersCount} au total)`);
      } else if (count > baseline.current && pollCount.current % 3 === 0) {
        playChime();
      }
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 20000);
    return () => clearInterval(id);
  }, [poll]);

  // Re-sync push subscription on mount (deployments unregister the old SW)
  useEffect(() => {
    if (!pushSupported) return;
    (async () => {
      try {
        if (Notification.permission === "granted" && localStorage.getItem(PUSH_KEY) === "1") {
          await subscribeAdminPush();
          setPushOn(true);
        }
      } catch (_e) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enablePush = async () => {
    if (!pushSupported) {
      toast.error("Notifications push non supportées par ce navigateur.");
      return;
    }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permission refusée. Activez les notifications dans les réglages du navigateur.");
        return;
      }
      await subscribeAdminPush();
      localStorage.setItem(PUSH_KEY, "1");
      setPushOn(true);
      toast.success("Notifications push activées sur cet appareil ✓");
      try {
        await api.post("/push/test");
      } catch (_e) { /* ignore */ }
    } catch (e) {
      toast.error("Impossible d'activer les notifications push.");
    } finally {
      setPushBusy(false);
    }
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem(SOUND_KEY, next ? "1" : "0");
    if (next) playChime();
  };

  const extraPending = pendingPayouts + pendingVerifications + pendingPartners;
  return (
    <div
      className={`mb-5 rounded-xl border px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3 ${
        pending > 0 || extraPending > 0
          ? "border-amber-300 bg-amber-50"
          : "border-gray-200 bg-white"
      }`}
      data-testid="admin-alerts-bar"
    >
      <div className="flex items-center gap-2 mr-auto">
        <span
          className={`w-8 h-8 rounded-full grid place-items-center ${
            pending > 0 ? "bg-amber-100 text-amber-700 animate-pulse" : "bg-gray-100 text-gray-400"
          }`}
        >
          <Clock size={15} />
        </span>
        <div className="text-sm font-medium" data-testid="admin-pending-count">
          {pending > 0 ? (
            <span className="text-amber-800">
              {pending} transaction{pending > 1 ? "s" : ""} en attente
            </span>
          ) : (
            <span className="text-gray-500">Aucune transaction en attente</span>
          )}
        </div>
      </div>

      {pendingVerifications > 0 && (
        <Link
          to="/admin/verifications"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition"
          data-testid="admin-pending-verifications"
        >
          <BadgeCheck size={13} /> {pendingVerifications} KYC à vérifier
        </Link>
      )}
      {pendingPayouts > 0 && (
        <Link
          to="/admin/parrainage"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition"
          data-testid="admin-pending-payouts"
        >
          <Wallet size={13} /> {pendingPayouts} demande{pendingPayouts > 1 ? "s" : ""} de paiement
        </Link>
      )}
      {pendingPartners > 0 && (
        <Link
          to="/admin/partenaires"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition"
          data-testid="admin-pending-partners"
        >
          <Handshake size={13} /> {pendingPartners} candidature{pendingPartners > 1 ? "s" : ""} partenaire
        </Link>
      )}

      <button
        type="button"
        onClick={toggleSound}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
          soundOn
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-gray-50 text-gray-500 border-gray-200"
        }`}
        title="Alerte sonore quand une transaction est en attente"
        data-testid="toggle-sound-alert"
      >
        {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
        {soundOn ? "Son activé" : "Son coupé"}
      </button>

      {pushSupported && (
        <button
          type="button"
          onClick={pushOn ? undefined : enablePush}
          disabled={pushOn || pushBusy}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition disabled:cursor-default ${
            pushOn
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-[var(--sl-primary)] text-white border-[var(--sl-primary)] hover:opacity-90"
          }`}
          title="Recevoir une notification sur cet appareil même quand l'app est fermée"
          data-testid="enable-push-btn"
        >
          {pushOn ? <BellRing size={13} /> : <Bell size={13} />}
          {pushOn ? "Push actif sur cet appareil" : pushBusy ? "Activation…" : "Activer les notifications push"}
        </button>
      )}
    </div>
  );
}
